<template>
  <div class="dialog">
    <span class="cls" @click="close"></span>
    <div class="paper">
      <!-- Header -->
      <div class="header">
        <span class="back" @click="close">
          <i class="ph ph-caret-left"></i>
        </span>
        <h2>질문 상세</h2>
        <span class="more" v-if="isMyQuestion" @click="showMenu = !showMenu">
          <i class="ph ph-dots-three-vertical"></i>
        </span>
        <span class="placeholder" v-else></span>
        
        <!-- Dropdown Menu -->
        <div class="dropdown-menu" v-if="showMenu">
          <button @click="editQuestion">수정</button>
          <button @click="deleteQuestion" class="danger">삭제</button>
        </div>
      </div>

      <!-- Content -->
      <div class="content">
        <!-- Question Section -->
        <div class="question-section">
          <div class="q-header">
            <span class="q-icon">Q</span>
            <span class="category" v-if="question.category">#{{ question.category }}</span>
          </div>
          <h3 class="q-title">{{ question.title }}</h3>
          <p class="q-content">{{ question.content }}</p>
          <div class="q-meta">
            <span>{{ question.mb_id }}</span>
            <span>·</span>
            <span>{{ formatDate(question.created_at) }}</span>
            <span>·</span>
            <span>조회 {{ question.view_count }}</span>
          </div>
        </div>

        <!-- Answers Section -->
        <div class="answers-section">
          <div class="section-header">
            <span class="a-icon">A</span>
            <span>답변 {{ answers.length }}개</span>
          </div>

          <!-- Answer List -->
          <div class="answer-list">
            <div 
              class="answer-item" 
              v-for="answer in answers" 
              :key="answer.idx"
              :class="{ adopted: answer.is_adopted }"
            >
              <div class="answer-header">
                <span class="adopted-badge" v-if="answer.is_adopted">채택됨</span>
                <span class="answer-author">{{ answer.mb_id }}</span>
                <span class="answer-date">{{ formatDate(answer.created_at) }}</span>
                
                <!-- 답변 수정/삭제 메뉴 -->
                <span 
                  class="answer-menu" 
                  v-if="answer.mb_id === mb_id"
                  @click="toggleAnswerMenu(answer.idx)"
                >
                  <i class="ph ph-dots-three-vertical"></i>
                </span>
                
                <div class="answer-dropdown" v-if="activeAnswerMenu === answer.idx">
                  <button @click="startEditAnswer(answer)">수정</button>
                  <button @click="deleteAnswer(answer.idx)" class="danger">삭제</button>
                </div>
              </div>

              <!-- 답변 내용 (수정 모드) -->
              <div v-if="editingAnswer === answer.idx" class="edit-mode">
                <textarea 
                  v-model="editContent" 
                  rows="3"
                ></textarea>
                <div class="edit-actions">
                  <button class="cancel-btn" @click="cancelEdit">취소</button>
                  <button class="save-btn" @click="saveAnswer(answer.idx)">저장</button>
                </div>
              </div>

              <!-- 답변 내용 (일반 모드) -->
              <p class="answer-content" v-else>{{ answer.content }}</p>
              
              <!-- 채택 버튼 -->
              <button 
                class="adopt-btn" 
                v-if="isMyQuestion && !question.adopted_idx && !answer.is_adopted"
                @click="adoptAnswer(answer.idx)"
              >
                채택하기
              </button>

              <!-- 대댓글 토글 버튼 -->
              <button 
                class="reply-toggle-btn" 
                @click="toggleReplyInput(answer.idx)"
                v-if="editingAnswer !== answer.idx"
              >
                <i class="ph ph-chat-circle-dots"></i>
                {{ answer.replies?.length ? `답글 ${answer.replies.length}개` : '답글 달기' }}
              </button>

              <!-- 대댓글 목록 -->
              <div class="reply-list" v-if="answer.replies && answer.replies.length > 0">
                <div 
                  class="reply-item" 
                  v-for="reply in answer.replies" 
                  :key="reply.idx"
                >
                  <div class="reply-header">
                    <i class="ph ph-corner-down-right"></i>
                    <span class="reply-author">{{ reply.mb_id }}</span>
                    <span class="reply-date">{{ formatDate(reply.created_at) }}</span>
                    
                    <!-- 대댓글 삭제 -->
                    <span 
                      class="reply-delete" 
                      v-if="reply.mb_id === mb_id"
                      @click="deleteReply(reply.idx, answer.idx)"
                    >
                      <i class="ph ph-trash"></i>
                    </span>
                  </div>
                  <p class="reply-content">{{ reply.content }}</p>
                </div>
              </div>

              <!-- 대댓글 입력 -->
              <div class="reply-input" v-if="activeReplyInput === answer.idx">
                <textarea 
                  v-model="replyContent[answer.idx]" 
                  placeholder="답글을 입력하세요"
                  rows="2"
                ></textarea>
                <div class="reply-actions">
                  <button class="cancel-btn" @click="toggleReplyInput(null)">취소</button>
                  <button 
                    class="submit-btn" 
                    @click="submitReply(answer.idx)"
                    :disabled="!replyContent[answer.idx]?.trim()"
                  >
                    작성
                  </button>
                </div>
              </div>
            </div>

            <div class="empty-state" v-if="answers.length === 0">
              <p>아직 답변이 없습니다</p>
              <p class="sub">첫 번째 답변을 작성해보세요!</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Answer Input -->
      <div class="answer-input" v-if="!isMyQuestion">
        <textarea 
          v-model="newAnswer" 
          placeholder="답변을 작성해주세요"
          rows="3"
        ></textarea>
        <button 
          class="submit-btn" 
          @click="submitAnswer"
          :disabled="!newAnswer.trim()"
        >
          <i class="ph ph-paper-plane-tilt"></i>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, inject, computed, onMounted, reactive } from "vue"

export default {
  props: {
    oth_showQnAView: Function,
    oth_showQnaEdit: Function,
    questionIdx: { required: true }
  },
  setup(props) {
    const sql_query = inject('sql_query')
    const member_info = inject('member_info')
    const mb_id = member_info.mb_id

    const question = ref({})
    const answers = ref([])
    const newAnswer = ref('')
    const showMenu = ref(false)
    
    // 답변 수정/삭제 관련
    const activeAnswerMenu = ref(null)
    const editingAnswer = ref(null)
    const editContent = ref('')

    // 대댓글 관련
    const activeReplyInput = ref(null)
    const replyContent = reactive({})

    const isMyQuestion = computed(() => {
      return question.value.mb_id === mb_id
    })

    // 질문 조회
    const loadQuestion = async () => {
      await sql_query(`
        UPDATE qna_question 
        SET view_count = view_count + 1 
        WHERE idx = ${props.questionIdx}
      `)

      const rows = await sql_query(`
        SELECT idx, mb_id, category, title, content, 
               view_count, answer_count, adopted_idx, created_at
        FROM qna_question
        WHERE idx = ${props.questionIdx}
      `)
      
      if (rows.length > 0) {
        question.value = rows[0]
      }
    }

    // 답변 목록 조회 (대댓글 포함)
    const loadAnswers = async () => {
      const rows = await sql_query(`
        SELECT * FROM qna_answer 
        WHERE question_idx = ${props.questionIdx}
        ORDER BY created_at ASC
      `)
      
      // 각 답변에 대댓글 불러오기
      for (let answer of rows) {
        const replies = await sql_query(`
          SELECT * FROM qna_reply 
          WHERE answer_idx = ${answer.idx}
          ORDER BY created_at ASC
        `)
        answer.replies = replies
      }
      
      answers.value = rows
    }

    // 답변 등록
    const submitAnswer = async () => {
      if (!newAnswer.value.trim()) return

      await sql_query(`
        INSERT INTO qna_answer (question_idx, mb_id, content)
        VALUES (${props.questionIdx}, '${mb_id}', '${newAnswer.value.replace(/'/g, "''")}')
      `)

      await sql_query(`
        UPDATE qna_question 
        SET answer_count = answer_count + 1 
        WHERE idx = ${props.questionIdx}
      `)

      newAnswer.value = ''
      reload()
    }

    // 답변 메뉴 토글
    const toggleAnswerMenu = (idx) => {
      activeAnswerMenu.value = activeAnswerMenu.value === idx ? null : idx
    }

    // 답변 수정 시작
    const startEditAnswer = (answer) => {
      editingAnswer.value = answer.idx
      editContent.value = answer.content
      activeAnswerMenu.value = null
    }

    // 답변 수정 취소
    const cancelEdit = () => {
      editingAnswer.value = null
      editContent.value = ''
    }

    // 답변 저장
    const saveAnswer = async (answerIdx) => {
      if (!editContent.value.trim()) return

      await sql_query(`
        UPDATE qna_answer 
        SET content = '${editContent.value.replace(/'/g, "''")}'
        WHERE idx = ${answerIdx} AND mb_id = '${mb_id}'
      `)

      editingAnswer.value = null
      editContent.value = ''
      reload()
    }

    // 답변 삭제
    const deleteAnswer = async (answerIdx) => {
      if (!confirm('답변을 삭제하시겠습니까?')) return
      
      activeAnswerMenu.value = null

      // 대댓글도 함께 삭제됨 (ON DELETE CASCADE)
      await sql_query(`
        DELETE FROM qna_answer 
        WHERE idx = ${answerIdx} AND mb_id = '${mb_id}'
      `)

      await sql_query(`
        UPDATE qna_question 
        SET answer_count = answer_count - 1 
        WHERE idx = ${props.questionIdx}
      `)

      reload()
    }

    // 답변 채택
    const adoptAnswer = async (answerIdx) => {
      await sql_query(`
        UPDATE qna_question 
        SET adopted_idx = ${answerIdx} 
        WHERE idx = ${props.questionIdx}
      `)
      reload()
    }

    // 대댓글 입력창 토글
    const toggleReplyInput = (answerIdx) => {
      activeReplyInput.value = activeReplyInput.value === answerIdx ? null : answerIdx
    }

    // 대댓글 작성
    const submitReply = async (answerIdx) => {
      const content = replyContent[answerIdx]?.trim()
      if (!content) return

      await sql_query(`
        INSERT INTO qna_reply (answer_idx, mb_id, content)
        VALUES (${answerIdx}, '${mb_id}', '${content.replace(/'/g, "''")}')
      `)

      replyContent[answerIdx] = ''
      activeReplyInput.value = null
      reload()
    }

    // 대댓글 삭제
    const deleteReply = async (replyIdx) => {
      if (!confirm('답글을 삭제하시겠습니까?')) return

      await sql_query(`
        DELETE FROM qna_reply 
        WHERE idx = ${replyIdx} AND mb_id = '${mb_id}'
      `)

      reload()
    }

    // 질문 수정
    const editQuestion = () => {
      showMenu.value = false
      props.oth_showQnaEdit(true, { editIdx: props.questionIdx })
    }

    // 질문 삭제
    const deleteQuestion = async () => {
      if (!confirm('정말 삭제하시겠습니까?')) return
      
      showMenu.value = false
      
      await sql_query(`
        DELETE FROM qna_answer WHERE question_idx = ${props.questionIdx}
      `)
      await sql_query(`
        DELETE FROM qna_question WHERE idx = ${props.questionIdx} AND mb_id = '${mb_id}'
      `)
      
      props.oth_showQnAView(false, { refresh: true })
    }

    const close = () => {
      props.oth_showQnAView(false)
    }

    const formatDate = (dateStr) => {
      if (!dateStr) return ''
      const date = new Date(dateStr)
      const now = new Date()
      const diff = now - date
      
      if (diff < 60000) return '방금 전'
      if (diff < 3600000) return `${Math.floor(diff / 60000)}분 전`
      if (diff < 86400000) return `${Math.floor(diff / 3600000)}시간 전`
      if (diff < 604800000) return `${Math.floor(diff / 86400000)}일 전`
      
      return `${date.getMonth() + 1}/${date.getDate()}`
    }

    async function reload() {
      await loadQuestion()
      await loadAnswers()
    }

    onMounted(() => {
      reload()
    })

    return {
      question,
      answers,
      newAnswer,
      showMenu,
      isMyQuestion,
      mb_id,
      activeAnswerMenu,
      editingAnswer,
      editContent,
      activeReplyInput,
      replyContent,
      submitAnswer,
      toggleAnswerMenu,
      startEditAnswer,
      cancelEdit,
      saveAnswer,
      deleteAnswer,
      adoptAnswer,
      toggleReplyInput,
      submitReply,
      deleteReply,
      editQuestion,
      deleteQuestion,
      close,
      formatDate
    }
  }
}
</script>

<style scoped>
.dialog {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  background: var(--color-surface);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: var(--z-index-top);
  backdrop-filter: blur(var(--effect-blur));
}

.dialog > .cls {
  width: 100%;
  height: 100%;
  position: absolute;
  left: 0;
  top: 0;
  z-index: -1;
}

.dialog > .paper {
  width: 90%;
  max-width: 600px;
  height: 85%;
  background: var(--color-background);
  border: 1px solid var(--color-background-light);
  border-radius: var(--radius-lg);
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* Header */
.header {
  width: 100%;
  height: 56px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 16px;
  box-sizing: border-box;
  flex-shrink: 0;
  border-bottom: 1px solid var(--color-background-light);
  position: relative;
}

.header > h2 {
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-primary);
  margin: 0;
}

.header > span {
  width: 40px;
  height: 40px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
}

.header > span.placeholder {
  cursor: default;
}

.header > span > i {
  font-size: var(--font-size-xl);
  color: var(--color-text-primary);
}

.dropdown-menu {
  position: absolute;
  top: 50px;
  right: 16px;
  background: var(--color-background);
  border: 1px solid var(--color-background-light);
  border-radius: var(--radius-md);
  overflow: hidden;
  box-shadow: var(--shadow-md);
  z-index: var(--z-index-base);
}

.dropdown-menu button {
  display: block;
  width: 100%;
  padding: 12px 20px;
  border: none;
  background: none;
  text-align: left;
  font-size: var(--font-size-sm);
  color: var(--color-text-primary);
  cursor: pointer;
}

.dropdown-menu button:hover {
  background: var(--color-background-light);
}

.dropdown-menu button.danger {
  color: var(--color-special-warning);
}

/* Content */
.content {
  flex: 1;
  overflow-y: auto;
}

/* Question Section */
.question-section {
  padding: 20px 16px;
  border-bottom: 8px solid var(--color-background-light);
}

.q-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
}

.q-icon {
  width: 28px;
  height: 28px;
  background: var(--color-brand);
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: var(--font-weight-bold);
  font-size: 14px;
  color: var(--color-text-white);
}

.category {
  font-size: var(--font-size-sm);
  color: var(--color-special-point);
}

.q-title {
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-primary);
  margin: 0 0 12px 0;
  line-height: 1.4;
}

.q-content {
  font-size: var(--font-size-base);
  color: var(--color-text-primary);
  line-height: 1.6;
  margin: 0 0 16px 0;
  white-space: pre-wrap;
}

.q-meta {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: var(--font-size-xs);
  color: var(--color-text-third);
}

/* Answers Section */
.answers-section {
  padding: 16px;
}

.section-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
}

.a-icon {
  width: 28px;
  height: 28px;
  background: var(--color-special-point);
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: var(--font-weight-bold);
  font-size: 14px;
  color: var(--color-text-white);
}

.section-header > span:last-child {
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-medium);
  color: var(--color-text-primary);
}

/* Answer List */
.answer-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.answer-item {
  padding: 16px;
  background: var(--color-background-light);
  border-radius: 12px;
  position: relative;
}

.answer-item.adopted {
  background: var(--color-surface-light);
  border: 1px solid var(--color-special-point);
}

.answer-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  position: relative;
}

.adopted-badge {
  padding: 4px 8px;
  background: var(--color-special-point);
  color: var(--color-text-white);
  font-size: 11px;
  font-weight: var(--font-weight-bold);
  border-radius: var(--radius-sm);
}

.answer-author {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-text-primary);
}

.answer-date {
  font-size: var(--font-size-xs);
  color: var(--color-text-third);
  flex: 1;
}

.answer-menu {
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border-radius: 4px;
}

.answer-menu:hover {
  background: var(--color-background);
}

.answer-menu i {
  font-size: 16px;
  color: var(--color-text-secondary);
}

.answer-dropdown {
  position: absolute;
  top: 30px;
  right: 0;
  background: var(--color-background);
  border: 1px solid var(--color-background-light);
  border-radius: var(--radius-md);
  overflow: hidden;
  box-shadow: var(--shadow-md);
  z-index: 10;
}

.answer-dropdown button {
  display: block;
  width: 100%;
  padding: 10px 16px;
  border: none;
  background: none;
  text-align: left;
  font-size: var(--font-size-sm);
  color: var(--color-text-primary);
  cursor: pointer;
  white-space: nowrap;
}

.answer-dropdown button:hover {
  background: var(--color-background-light);
}

.answer-dropdown button.danger {
  color: var(--color-special-warning);
}

.answer-content {
  font-size: var(--font-size-base);
  color: var(--color-text-primary);
  line-height: 1.6;
  margin: 0;
  white-space: pre-wrap;
}

/* 답변 수정 모드 */
.edit-mode {
  margin-bottom: 12px;
}

.edit-mode textarea {
  width: 100%;
  padding: 12px;
  border: 1px solid var(--color-brand);
  border-radius: 8px;
  font-size: var(--font-size-base);
  color: var(--color-text-primary);
  background: var(--color-background);
  resize: vertical;
  min-height: 80px;
  box-sizing: border-box;
}

.edit-mode textarea:focus {
  outline: none;
}

.edit-actions {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  margin-top: 8px;
}

.cancel-btn, .save-btn {
  padding: 8px 16px;
  border-radius: 6px;
  font-size: var(--font-size-sm);
  cursor: pointer;
  border: none;
}

.cancel-btn {
  background: var(--color-background-light);
  color: var(--color-text-secondary);
}

.save-btn {
  background: var(--color-brand);
  color: var(--color-text-white);
}

.adopt-btn {
  margin-top: 12px;
  padding: 8px 16px;
  border: 1px solid var(--color-brand);
  border-radius: 6px;
  background: transparent;
  color: var(--color-brand);
  font-size: var(--font-size-sm);
  cursor: pointer;
}

.adopt-btn:hover {
  background: var(--color-brand);
  color: var(--color-text-white);
}

.reply-toggle-btn {
  margin-top: 12px;
  padding: 6px 12px;
  border: 1px solid var(--color-background-light);
  border-radius: 6px;
  background: transparent;
  color: var(--color-text-secondary);
  font-size: var(--font-size-sm);
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
}

.reply-toggle-btn:hover {
  background: var(--color-background);
}

.reply-toggle-btn i {
  font-size: 16px;
}

/* 대댓글 목록 */
.reply-list {
  margin-top: 12px;
  padding-left: 16px;
  border-left: 2px solid var(--color-background-light);
}

.reply-item {
  padding: 10px 0;
  border-bottom: 1px solid var(--color-background-light);
}

.reply-item:last-child {
  border-bottom: none;
}

.reply-header {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 6px;
}

.reply-header > i {
  font-size: 14px;
  color: var(--color-text-third);
}

.reply-author {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-text-primary);
}

.reply-date {
  font-size: var(--font-size-xs);
  color: var(--color-text-third);
  flex: 1;
}

.reply-delete {
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border-radius: 4px;
}

.reply-delete:hover {
  background: var(--color-background);
}

.reply-delete i {
  font-size: 14px;
  color: var(--color-special-warning);
}

.reply-content {
  font-size: var(--font-size-sm);
  color: var(--color-text-primary);
  line-height: 1.5;
  margin: 0;
  white-space: pre-wrap;
}

/* 대댓글 입력 */
.reply-input {
  margin-top: 12px;
  padding: 12px;
  background: var(--color-background);
  border-radius: 8px;
}

.reply-input textarea {
  width: 100%;
  padding: 10px;
  border: 1px solid var(--color-background-light);
  border-radius: 6px;
  font-size: var(--font-size-sm);
  color: var(--color-text-primary);
  background: var(--color-background-light);
  resize: none;
  box-sizing: border-box;
}

.reply-input textarea:focus {
  outline: none;
  border-color: var(--color-brand);
}

.reply-actions {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  margin-top: 8px;
}

.reply-actions .submit-btn {
  padding: 6px 14px;
  border-radius: 6px;
  font-size: var(--font-size-sm);
  cursor: pointer;
  border: none;
  background: var(--color-brand);
  color: var(--color-text-white);
}

.reply-actions .submit-btn:disabled {
  background: var(--color-background-light);
  color: var(--color-text-third);
  cursor: not-allowed;
}

.empty-state {
  padding: 32px 0;
  text-align: center;
}

.empty-state > p {
  margin: 0;
  color: var(--color-text-secondary);
}

.empty-state > p.sub {
  font-size: var(--font-size-sm);
  color: var(--color-text-third);
  margin-top: 4px;
}

/* Answer Input */
.answer-input {
  display: flex;
  gap: 12px;
  padding: 16px;
  border-top: 1px solid var(--color-background-light);
  flex-shrink: 0;
  align-items: flex-end;
}

.answer-input textarea {
  flex: 1;
  padding: 12px;
  border: 1px solid var(--color-background-light);
  border-radius: 12px;
  font-size: var(--font-size-base);
  color: var(--color-text-primary);
  background: var(--color-background-light);
  resize: none;
}

.answer-input textarea:focus {
  outline: none;
  border-color: var(--color-brand);
}

.answer-input textarea::placeholder {
  color: var(--color-text-third);
}

.answer-input .submit-btn {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  border: none;
  background: var(--color-brand);
  color: var(--color-text-white);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.answer-input .submit-btn:disabled {
  background: var(--color-background-light);
  cursor: not-allowed;
}

.answer-input .submit-btn i {
  font-size: 20px;
}
</style>